import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import isString from 'lodash/isString';
import pickBy from 'lodash/pickBy';
import isUndefined from 'lodash/isUndefined';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';

import {insightsFilterToPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {DATASET_MIXPANEL, PropertyType} from 'common/report/constants';
import {BaseTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {ProfileHistoryOptions} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FilterValue} from 'common/widgets/property-filter-menu/models/types';
import {FilterType} from 'common/components/filter-bar/types';
import {GroupByCohort} from 'common/report/insights/util/types';

import {InsightsResourceType} from '../util/types';
import {GroupClause as GroupClauseType} from '../util/types';
import {INSIGHTS_GROUP_PROPERTIES, TIME_UNIT_GROUPING_LIST, TIME_UNITS} from './constants';
import {sanitizeIfCustomProperty} from './utils';
import {ClauseType} from './types';

const CREATE_CUSTOM_PROPERTY = {
  icon: `compose`,
  name: `$custom_property`,
  type: `custom`,
  special: true,
  hasPill: false,
};

export class GroupClause {
  static TYPE: typeof ClauseType.Group = ClauseType.Group;
  static EVENT_DATE = {
    label: `Date`,
    icon: `star-top-events`,
    name: `$date`,
    type: `datetime`,
    resourceType: `events`,
    special: true,
  };

  static CREATE_CUSTOM_EVENT_PROPERTY = Object.assign(
    {label: `Create event property...`, resourceType: `events`},
    CREATE_CUSTOM_PROPERTY,
  );
  static CREATE_CUSTOM_PEOPLE_PROPERTY = Object.assign(
    {label: `Create people property...`, resourceType: `people`},
    CREATE_CUSTOM_PROPERTY,
  );
  static PropertyType = Object.values(PropertyType);
  static PROPERTY_TYPECASTS = [`string`, `number`, `boolean`];

  behavior?: GroupClauseType['behavior'];
  customBucket?: GroupClauseType['customBucket'];
  customGroups?: GroupClauseType['customGroups'];
  customProperty?: GroupClauseType['customProperty'];
  customPropertyId?: GroupClauseType['customPropertyId'];
  dataGroupId?: GroupClauseType['dataGroupId'];
  dataset: GroupClauseType['dataset'];
  datasetId?: GroupClauseType['datasetId'];
  editing: GroupClauseType['editing'] | null;
  joinPropertyType?: GroupClauseType['joinPropertyType'];
  listItemGroup?: GroupClauseType['listItemGroup'];
  listQuantifier?: GroupClauseType['listQuantifier'];
  profileType?: GroupClauseType['profileType'];
  projectId?: GroupClauseType['projectId'];
  propertyObjectKey?: GroupClauseType['propertyObjectKey'];
  propertyType?: GroupClauseType['propertyType'];
  resourceType: GroupClauseType['resourceType'];
  search?: GroupClauseType['search'];
  tempDataGroupId?: GroupClauseType['tempDataGroupId'];
  typeCast?: GroupClauseType['typeCast'];
  unit?: GroupClauseType['unit'];
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  value: GroupClauseType['value'] | null;

  // Fields not included on GroupClauseType
  cohorts?: GroupByCohort[];
  filterOperator: TODO;
  filterDateUnit: TimeUnit;
  filterJoinType: TODO;
  filterType: FilterType;
  filterValue: FilterValue;
  propertyFilter: PropertyFilter;
  profileHistoryOptions?: ProfileHistoryOptions;

  scdEvent?: GroupClauseType['scdEvent'];

  // for funnel step ttc step selection
  startStepNumber?: GroupClauseType['startStepNumber'];
  endStepNumber?: GroupClauseType['endStepNumber'];

  private clauseConstructor({dataset = undefined, behavior = undefined}: {dataset?: string; behavior?: any}) {
    dataset = behavior?.dataset || dataset;
    this.dataset = dataset === `mixpanel` ? DATASET_MIXPANEL : dataset;
  }

  private eventsConstructor(attrs: any) {
    this.clauseConstructor(attrs);
    this.value = attrs.value || null;
    this.resourceType = attrs.resourceType || InsightsResourceType.Events;
    this.profileType = attrs.profileType || null;
    this.search = attrs.search || ``;
    this.dataGroupId = attrs.dataGroupId || null;
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }

    Object.assign(this, sanitizeIfCustomProperty(pick(attrs, `customPropertyId`, `customProperty`) as any));
  }

  constructor(attrs: Partial<GroupClauseType> = {}) {
    this.eventsConstructor(attrs);
    if (attrs.joinPropertyType) {
      this.joinPropertyType = attrs.joinPropertyType;
    }
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }
    this.propertyType = attrs.propertyType || null;
    this.typeCast = attrs.typeCast || this.getDefaultTypeCast(attrs);
    this.propertyObjectKey = attrs.propertyObjectKey;
    this.unit = attrs.unit || null;
    this.editing = attrs.editing || null;
    if (this.isDatetimeProperty && !this.editing && this.unit === undefined) {
      this.unit = TIME_UNITS.DAY;
    }
    if (attrs.customBucket) {
      this.customBucket = attrs.customBucket;
    }
    if (attrs.customGroups) {
      this.customGroups = attrs.customGroups;
    }
    if (attrs.behavior) {
      this.behavior = {
        ...attrs.behavior,
        // Cleanup: Either Behavior doesn't have custom properties or it needs to be added to the type.
        property: attrs.behavior.property && (sanitizeIfCustomProperty(attrs.behavior.property as any) as any),
      };
    }
    if (attrs.cohorts) {
      this.cohorts = attrs.cohorts;
    }

    //To determine whether it is a filter or not
    this.filterOperator = null;

    // Support for Single Segment in GroupClause (reassigns filterOperator here if necessary)
    for (const attrName of Object.keys(attrs)) {
      //Add all filter properties to the GroupClause if they exist
      if (!INSIGHTS_GROUP_PROPERTIES.includes(attrName)) {
        this[attrName] = attrs[attrName];
      }
    }

    //Make sure unit and filterDateUnit are equal if filterDateUnit exists
    if (this.filterDateUnit) {
      this.unit = this.filterDateUnit;
    }

    if (attrs.listQuantifier) {
      this.listQuantifier = attrs.listQuantifier;
    }
    if (attrs.listItemGroup) {
      this.listItemGroup = attrs.listItemGroup;
    }

    if (attrs.profileHistoryOptions) {
      this.profileHistoryOptions = attrs.profileHistoryOptions;
    }

    // propertyFilter is strictly used for rendering purposes since it contains the unflattened version of filterValue and the generic (non Insights specific) DateRangeType filterOperator]
    // also used to extract `filterValue` for boolean properties for Insights.
    this.propertyFilter = insightsFilterToPropertyFilter(this);

    if (isNumber(attrs.startStepNumber) || attrs.startStepNumber === null) {
      this.startStepNumber = attrs.startStepNumber;
    }
    if (isNumber(attrs.endStepNumber) || attrs.endStepNumber === null) {
      this.endStepNumber = attrs.endStepNumber;
    }
  }

  extend(attrs = {} as Partial<GroupClause['attrs']>) {
    return new GroupClause({...this.attrs, ...attrs});
  }

  get isCustomProperty() {
    return !!this.customProperty || !!this.customPropertyId;
  }

  isCustomPropertyValid() {
    return (
      this.customPropertyId ||
      (this.customProperty.name && this.customProperty.composedProperties && this.customProperty.displayFormula)
    );
  }

  get valid() {
    const eventValid =
      Object.values(InsightsResourceType).includes(this.resourceType) &&
      isString(this.search) &&
      (!this.isCustomProperty || this.isCustomPropertyValid());
    if (this.isGroupByCohorts()) {
      return this.isGroupByCohortsValid();
    }
    return eventValid && (this.isCustomProperty || this.isRegularPropertyValid());
  }

  isRegularPropertyValid() {
    const validIfDatetime = this.isDatetimeProperty
      ? !this.unit || TIME_UNIT_GROUPING_LIST.includes(this.unit as BaseTimeUnit)
      : true;

    return Object.values(GroupClause.PropertyType).includes(this.propertyType) && validIfDatetime;
  }

  isGroupByCohorts() {
    return !!this.cohorts && this.cohorts?.length > 0;
  }

  isGroupByCohortsValid() {
    const cohorts = this.cohorts || [];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return cohorts.every(({negated, raw_cohort, name, id}) => !!(isBoolean(negated) && (raw_cohort || (id && name))));
  }

  private clauseAttrs() {
    return pick(this, [`dataset`]);
  }

  private eventAttributes() {
    return {
      ...this.clauseAttrs(),
      ...pick(this, [
        `behavior`,
        `customProperty`,
        `customPropertyId`,
        `value`,
        `resourceType`,
        `profileType`,
        `search`,
        `dataGroupId`,
        `tempDataGroupId`,
        `scdEvent`,
      ]),
    };
  }

  private cohortAttributes(cohort: GroupByCohort): GroupByCohort {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {id, name, negated, raw_cohort} = cohort;
    return pickBy(
      {
        id,
        name,
        negated,
        raw_cohort,
      },
      (value) => !isUndefined(value),
    );
  }

  get attrs() {
    const {
      cohorts,
      joinPropertyType,
      propertyObjectKey,
      tempDataGroupId,
      propertyType,
      typeCast,
      unit,
      customBucket,
      customGroups,
      filterOperator,
      filterValue,
      filterType,
      listQuantifier,
      listItemGroup,
      filterJoinType,
      profileHistoryOptions,
      startStepNumber,
      endStepNumber,
    } = this;
    const isFilter = !isNil(this.filterOperator);
    const type = listItemGroup?.typeCast || listItemGroup?.propertyType || typeCast || propertyType;
    return {
      ...this.eventAttributes(),
      ...(cohorts ? {cohorts} : {}),
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      propertyType,
      ...(isFilter ? {} : {typeCast}),
      ...(isFilter ? {defaultType: propertyType} : {}),
      ...(isFilter ? {filterDateUnit: unit} : {unit}),
      ...(customBucket && type === PropertyType.Number ? {customBucket} : {}),
      ...(customGroups && [PropertyType.String, PropertyType.List].includes(type) ? {customGroups} : {}),
      ...(filterOperator ? {filterOperator} : {}),
      ...(!isNil(filterValue) ? {filterValue} : {}),
      ...(filterType ? {filterType} : {}),
      ...(listQuantifier ? {listQuantifier} : {}),
      ...(listItemGroup ? {listItemGroup} : {}),
      ...(filterJoinType ? {filterJoinType} : {}),
      ...(profileHistoryOptions ? {profileHistoryOptions} : {}),
      ...(isNumber(startStepNumber) || startStepNumber === null ? {startStepNumber} : {}),
      ...(isNumber(endStepNumber) || endStepNumber === null ? {endStepNumber} : {}),
    };
  }

  private eventToUrlData() {
    return {
      ...this.attrs,
      ...pick(this.attrs, [
        `behavior`,
        `customProperty`,
        `customPropertyId`,
        `value`,
        `resourceType`,
        `profileType`,
        `dataGroupId`,
        `tempDataGroupId`,
      ]),
    };
  }

  toUrlData() {
    const conditionalAttrs: {
      customBucket?: Bucket;
      customGroups?: TODO;
      propertyType: PropertyType;
      typeCast?: PropertyType;
      unit?: TimeUnit;
      cohorts?: GroupByCohort[];
    } = {propertyType: this.propertyType};
    if (this.isDatetimeProperty) {
      conditionalAttrs.unit = this.unit || TIME_UNITS.DAY;
    }
    if (this.typeCast) {
      conditionalAttrs.typeCast = this.typeCast;
    }
    if (this.customBucket) {
      conditionalAttrs.customBucket = this.customBucket;
    }
    if (this.customGroups) {
      conditionalAttrs.customGroups = this.customGroups;
    }
    if (this.cohorts) {
      conditionalAttrs.cohorts = this.cohorts.map(this.cohortAttributes);
    }
    return {...this.eventToUrlData(), ...conditionalAttrs};
  }

  serialize() {
    return this.attrs;
  }

  trackingData() {
    return this.serialize();
  }

  toBookmarkData() {
    return pickBy(this.attrs, (value) => !isUndefined(value));
  }

  getDefaultTypeCast(attrs): PropertyType | null {
    if (attrs.value === `campaign_id`) {
      return PropertyType.String;
    }

    return null;
  }

  get isDatetimeProperty() {
    const realType = this.typeCast || this.propertyType;
    return realType === `datetime`;
  }
}
